import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { del, get, getAccessToken, patch, post } from './client.ts'
import { uploadPhoto } from '../lib/image.ts'
import type { Activation } from '../components/ActivateDialog.tsx'
import type {
  Appeal,
  AuditEntry,
  CoClaim,
  LedgerView,
  PayoutWeek,
  Settings,
  TaskDefinition,
  User,
  WeekHistoryRow,
  WeekView,
} from './types.ts'

export const keys = {
  week: (userId?: string) => ['week', userId ?? 'me'] as const,
  ledger: (weekId: string) => ['ledger', weekId] as const,
  history: (userId?: string) => ['history', userId ?? 'me'] as const,
  users: ['users'] as const,
  appeals: (userId?: string) => ['appeals', userId ?? 'all'] as const,
  coClaims: ['co-claims'] as const,
  payouts: ['payouts'] as const,
  audit: ['audit'] as const,
  tasks: ['tasks'] as const,
  settings: ['settings'] as const,
}

export function useWeek(userId?: string) {
  return useQuery({
    queryKey: keys.week(userId),
    queryFn: () => get<WeekView>(`/api/weeks/current${userId ? `?userId=${userId}` : ''}`),
    // El saldo tiene que estar fresco: es lo que mira todo el mundo.
    staleTime: 10_000,
  })
}

export function useLedger(weekId: string | undefined) {
  return useQuery({
    queryKey: keys.ledger(weekId ?? ''),
    queryFn: () => get<LedgerView>(`/api/weeks/${weekId}/ledger`),
    enabled: Boolean(weekId),
  })
}

export function useHistory(userId?: string) {
  return useQuery({
    queryKey: keys.history(userId),
    queryFn: () =>
      get<WeekHistoryRow[]>(`/api/weeks/history${userId ? `?userId=${userId}` : ''}`),
  })
}

export function useAppeals(params: { userId?: string; onlyOpen?: boolean } = {}) {
  const search = new URLSearchParams()
  if (params.userId) search.set('userId', params.userId)
  if (params.onlyOpen) search.set('open', 'true')
  const suffix = search.toString() ? `?${search}` : ''
  return useQuery({
    queryKey: [...keys.appeals(params.userId), params.onlyOpen ?? false],
    queryFn: () => get<Appeal[]>(`/api/appeals${suffix}`),
  })
}

export function useAudit(params: { date?: string; subject?: string } = {}) {
  const search = new URLSearchParams()
  if (params.date) search.set('date', params.date)
  if (params.subject) search.set('subject', params.subject)
  const suffix = search.toString() ? `?${search}` : ''
  return useQuery({
    queryKey: [...keys.audit, params.date ?? '', params.subject ?? ''],
    queryFn: () => get<AuditEntry[]>(`/api/audit${suffix}`),
  })
}

export function useCoClaims(params: { onlyOpen?: boolean } = {}) {
  return useQuery({
    queryKey: [...keys.coClaims, params.onlyOpen ?? false],
    queryFn: () => get<CoClaim[]>(`/api/co-claims${params.onlyOpen ? '?open=true' : ''}`),
  })
}

export function usePayouts() {
  return useQuery({ queryKey: keys.payouts, queryFn: () => get<PayoutWeek[]>('/api/weeks/payouts') })
}

/** Pedir y resolver un reparto mueven dinero en las semanas de los dos hermanos. */
function useSharedBonusMutation<T>(fn: (id: string, body: any) => Promise<T>) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: any }) => fn(id, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['week'] })
      void qc.invalidateQueries({ queryKey: ['ledger'] })
      void qc.invalidateQueries({ queryKey: keys.coClaims })
      void qc.invalidateQueries({ queryKey: keys.audit })
    },
  })
}

export const useCoClaim = () =>
  useSharedBonusMutation((id, body) => post(`/api/instances/${id}/co-claim`, body))

export const useResolveCoClaim = () =>
  useSharedBonusMutation((id, body) => post(`/api/co-claims/${id}/resolve`, body))

/**
 * Pone una tarea hoy, en la semana en curso. Con `markNotDone` nace ya
 * descontada, así que invalida también el desglose.
 */
export function useActivateTask() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body?: Activation }) =>
      post<{ created: number; assignees: string[]; deductedCents: number }>(
        `/api/tasks/${id}/activate`,
        body ?? {},
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['week'] })
      void qc.invalidateQueries({ queryKey: ['ledger'] })
      void qc.invalidateQueries({ queryKey: keys.audit })
    },
  })
}

/** Alegar y resolver mueven dinero, así que invalidan todo lo que lo muestra. */
function useAppealMutation<T>(fn: (id: string, body: any) => Promise<T>) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: any }) => fn(id, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['week'] })
      void qc.invalidateQueries({ queryKey: ['ledger'] })
      void qc.invalidateQueries({ queryKey: ['appeals'] })
      void qc.invalidateQueries({ queryKey: ['audit'] })
    },
  })
}

export const useCreateAppeal = () =>
  useAppealMutation((id, body) => post(`/api/instances/${id}/appeal`, body))

export const useResolveAppeal = () =>
  useAppealMutation((id, body) => post(`/api/appeals/${id}/resolve`, body))

export function useUsers() {
  return useQuery({ queryKey: keys.users, queryFn: () => get<User[]>('/api/users') })
}

export function useTasks() {
  return useQuery({ queryKey: keys.tasks, queryFn: () => get<TaskDefinition[]>('/api/tasks') })
}

export function useSettings() {
  return useQuery({ queryKey: keys.settings, queryFn: () => get<Settings>('/api/settings') })
}

/**
 * Cualquier accion sobre una instancia puede mover dinero, asi que se invalida
 * todo lo que muestra saldo. No se hace actualizacion optimista a proposito: el
 * importe que se ve tiene que venir del servidor, que es quien manda.
 */
function useInstanceMutation<T>(fn: (id: string, body?: any) => Promise<T>) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body?: any }) => fn(id, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['week'] })
      void qc.invalidateQueries({ queryKey: ['ledger'] })
    },
  })
}

export const useMarkNotDone = () =>
  useInstanceMutation((id, body) => post(`/api/instances/${id}/not-done`, body ?? {}))

export const useRestore = () =>
  useInstanceMutation((id, body) => post(`/api/instances/${id}/restore`, body))

export const useWarn = () =>
  useInstanceMutation((id, body) => post(`/api/instances/${id}/warning`, body ?? {}))

export const useClaim = () => useInstanceMutation((id) => post(`/api/instances/${id}/claim`))

export const useApprove = () => useInstanceMutation((id) => post(`/api/instances/${id}/approve`))

export const useReject = () =>
  useInstanceMutation((id, body) => post(`/api/instances/${id}/reject`, body))

/**
 * Sube la foto de prueba. Va aparte de `claim` a propósito: en un bonus abierto
 * la instancia no tiene dueño hasta reclamarla, así que primero se gana la
 * carrera y luego se adjunta la prueba.
 */
export function useUploadPhoto() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, file }: { id: string; file: File }) =>
      uploadPhoto(id, file, getAccessToken()),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['week'] }),
  })
}

export function useSaveTask() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id?: string; body: unknown }) =>
      id ? patch(`/api/tasks/${id}`, body) : post('/api/tasks', body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.tasks })
      void qc.invalidateQueries({ queryKey: ['week'] })
    },
  })
}

export function useArchiveTask() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => del(`/api/tasks/${id}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.tasks }),
  })
}

export function useSaveSettings() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: Partial<Settings>) => patch<Settings>('/api/settings', body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.settings })
      void qc.invalidateQueries({ queryKey: ['week'] })
    },
  })
}
